/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';

import { useRequest } from 'ahooks';
import { passport } from '@coze-studio/api-schema';
import {
  setUserInfo,
  useLoginStatus,
  useUserInfo,
  type UserInfo,
} from '@coze-foundation/account-adapter';

export const useLoginService = ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingProfileCompletion = useRef(false);

  const loginService = useRequest(
    async () => {
      const res = (await passport.PassportWebEmailLoginPost({
        email,
        password,
      })) as unknown as { data: UserInfo };
      return res.data;
    },
    {
      manual: true,
      onSuccess: data => {
        if (!data.phone_collected) {
          pendingProfileCompletion.current = true;
        }
        setUserInfo(data);
      },
    },
  );

  const loginStatus = useLoginStatus();
  const userInfo = useUserInfo();
  const googleLoginService = useRequest(
    async () => {
      const redirect = searchParams.get('redirect')?.trim();
      const googleStartPath = redirect
        ? `/api/auth/google/start?${new URLSearchParams({ redirect }).toString()}`
        : '/api/auth/google/start';
      const response = await fetch(googleStartPath, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to start Google sign-in');
      }
      return (await response.json()) as { auth_url: string };
    },
    {
      manual: true,
      onSuccess: result => {
        window.location.assign(result.auth_url);
      },
    },
  );

  useEffect(() => {
    if (loginStatus === 'logined') {
      if (pendingProfileCompletion.current || (userInfo && !userInfo.phone_collected)) {
        pendingProfileCompletion.current = false;
        const params = new URLSearchParams({
          google_auth: 'complete_profile',
          email: userInfo?.email ?? '',
        });
        const redirect = searchParams.get('redirect')?.trim();
        if (redirect) {
          params.set('redirect', redirect);
        }
        navigate(`/sign/register?${params.toString()}`);
        return;
      }
      const redirect = searchParams.get('redirect')?.trim();
      navigate(redirect || '/');
    }
  }, [loginStatus, navigate, searchParams, userInfo]);

  return {
    login: loginService.run,
    loginWithGoogle: googleLoginService.run,
    loginLoading: loginService.loading,
    googleLoading: googleLoginService.loading,
  };
};

export const useRegisterService = ({
  email,
  password,
  firstName,
  lastName,
  phoneNumber,
  organization,
}: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  organization: string;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const registerService = useRequest(
    async () => {
      const res = (await passport.PassportWebEmailRegisterV2Post({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber,
        organization: organization || undefined,
      })) as unknown as { data: UserInfo };
      return res.data;
    },
    {
      manual: true,
      onSuccess: data => {
        setUserInfo(data);
        const redirect = searchParams.get('redirect')?.trim();
        navigate(redirect || '/');
      },
    },
  );

  return {
    register: registerService.run,
    registerLoading: registerService.loading,
  };
};

export const useCompleteGoogleProfileService = ({
  firstName,
  lastName,
  phoneNumber,
  organization,
}: {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  organization: string;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const completeService = useRequest(
    async () => {
      const response = await fetch('/api/auth/google/complete-profile', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          phone_number: phoneNumber,
          organization: organization || undefined,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to complete Google profile');
      }
      return (await response.json()) as { data: UserInfo };
    },
    {
      manual: true,
      onSuccess: result => {
        setUserInfo(result.data);
        const redirect = searchParams.get('redirect')?.trim();
        navigate(redirect || '/');
      },
    },
  );

  return {
    completeGoogleProfile: completeService.run,
    completeGoogleProfileLoading: completeService.loading,
  };
};
